// Phase NOTARY-FOCUS N3 — /en/notary/coverage (EN) — 77-province nationwide coverage
import { RelatedHubGrid } from "@/components/RelatedHubGrid";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SnippetAnswer } from "@/components/seo/SnippetAnswer";
import { hreflangLinks, SEO_SITE, ctaDesc, ctaTitle } from "@/lib/seo";
import { areaServedSchema, NOTARY_PROVINCE_COUNT } from "@/data/notary-coverage";
import NotaryCoverageTable from "@/components/NotaryCoverageTable";
import RelatedNotaryLinks from "@/components/RelatedNotaryLinks";

import { ShieldCheck, MessageCircle, Phone, Truck } from "lucide-react";

const URL = `${SEO_SITE}/en/notary/coverage`;
const TITLE = "Notary Public Coverage in Thailand — All 77 Provinces | NYC Legal";
const DESC = "NYC Legal provides Notary Public services across all 77 provinces of Thailand via 4 branch offices. Get your timeline and checklist in minutes — chat now.";

export const Route = createFileRoute("/en/notary/coverage")({
  head: () => ({
    meta: [
      { title: ctaTitle(TITLE, "/en/notary/coverage", "en")}, { name: "description", content: ctaDesc(DESC, "/en/notary/coverage", "en")},
      { property: "og:title", content: ctaTitle(TITLE, "/en/notary/coverage", "en")},
      { property: "og:description", content: ctaDesc(DESC, "/en/notary/coverage", "en")},
      { property: "og:url", content: URL },
      { property: "og:locale", content: "en_US" },
    ],
    links: [{ rel: "canonical", href: URL }, ...hreflangLinks("/en/notary/coverage")],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org", "@type": "LegalService",
          name: "NYC Legal — Notary Public Coverage Thailand",
          url: URL, inLanguage: "en",
          areaServed: areaServedSchema(),
          serviceType: "Notarial Services Attorney",
          priceRange: "฿500–฿5,000",
          provider: { "@type": "LegalService", name: "NYC Language Institute", url: SEO_SITE },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org", "@type": "FAQPage", inLanguage: "en",
          mainEntity: [
            {
              "@type": "Question",
              name: "Does NYC offer Notary Public service outside Bangkok?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes — NYC Legal covers all 77 provinces of Thailand. We have branch offices in Bangkok (Lat Phrao 95), Khon Kaen, Udon Thani, and Nong Khai; local Notarial Attorney agents in Chiang Mai, Pattaya, Phuket, Hat Yai, Korat, Ubon Ratchathani, Surat Thani, and Ayutthaya; and Remote Signing + EMS courier for every other province within 1–3 business days.",
              },
            },
            {
              "@type": "Question",
              name: "How does Remote Signing Notary work in Thailand?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Send scanned documents and ID via LINE @nycli or email. Our team confirms price and timing within 30 minutes. You courier the originals to Bangkok via EMS or Kerry; a registered Notarial Services Attorney signs the same day and the documents are returned by EMS within 24 hours. Total turnaround: 2–3 business days. Service from THB 500 plus THB 80–150 courier.",
              },
            },
            {
              "@type": "Question",
              name: "Is there a Notary Public in Phuket, Pattaya or Chiang Mai?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes. NYC maintains local Notarial Attorney agents in Chiang Mai, Pattaya (Chon Buri), Phuket Town/Patong, and Hat Yai (Songkhla). In-person notarization can be arranged within one business day. Service from THB 800, including agent travel.",
              },
            },
          ],
        }),
      },
    ],
  }),
  component: EnNotaryCoveragePage,
});

function EnNotaryCoveragePage() {
  return (
    <main className="min-h-screen bg-white">
      <section className="relative bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_0%,hsl(var(--gold)/0.18),transparent_60%)]" aria-hidden />
        <div className="relative mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
          <nav aria-label="Breadcrumb" className="mb-4 text-[12px] text-white/80">
            <Link to="/en" className="hover:text-gold">Home</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/en/notary" className="hover:text-gold">Notary Public</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">Coverage</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <ShieldCheck className="h-3.5 w-3.5" /> All 77 Provinces · Notarial Services Attorney
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight sm:text-5xl">
            Notary Public Coverage in Thailand<br className="hidden sm:block" />
            <span className="text-gold">All 77 Provinces · Nationwide</span>
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            Wherever you are in Thailand — Bangkok, Chiang Mai, Phuket, Pattaya or the border — NYC Legal can notarize your documents within 1–3 business days through 4 branch offices, 10+ local agents and Remote Signing + EMS courier service.
          </p>
          <div className="mt-6 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md hover:-translate-y-0.5 transition">
              <MessageCircle className="h-4 w-4" /> Send documents on LINE
            </a>
            <a href="tel:0832494999"
              className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md hover:-translate-y-0.5 transition">
              <Phone className="h-4 w-4" /> +66 83-249-4999
            </a>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-10 sm:px-6 sm:py-14">
        <SnippetAnswer
          question="Where does NYC Legal provide Notary Public service in Thailand?"
          answer="NYC Legal provides Notary Public services across all 77 provinces of Thailand. Coverage includes 4 branch offices (Bangkok Lat Phrao 95, Khon Kaen, Udon Thani, Nong Khai), 10 local Notarial Attorney agents (Chiang Mai, Pattaya, Phuket, Hat Yai and more), and Remote Signing + EMS courier for every other province. Turnaround: 1–3 business days, from THB 500 per document."
          steps={{
            type: "ordered",
            items: [
              "Send scanned documents and ID via LINE @nycli or email",
              "Receive price and appointment confirmation within 30 minutes",
              "Courier original to Bangkok via EMS (or visit a local agent)",
              "Notarial Services Attorney signs the same day",
              "Documents returned by EMS / Kerry / Lalamove within 24 hours",
            ],
          }}
          lang="en"
        />
      </section>

      <section className="mx-auto max-w-6xl px-4 pb-16 sm:px-6">
        <h2 className="mb-6 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
          Coverage by Region · {NOTARY_PROVINCE_COUNT} Provinces
        </h2>
        <NotaryCoverageTable lang="en" />
      </section>

      <section className="mx-auto max-w-6xl px-4 pb-16 sm:px-6">
        <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-6">
          <div className="flex items-start gap-3">
            <Truck className="mt-1 h-6 w-6 text-amber-600" />
            <div>
              <h3 className="font-display text-lg font-extrabold text-navy-deep">
                Remote Signing + EMS — for clients in any province
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-700">
                If you can't travel to Bangkok, our team arranges nationwide
                pickup and return. Notary fee from THB 500 plus THB 80–150
                EMS / Kerry courier. Total turnaround 2–3 business days, ready
                for Apostille or embassy legalization the same week.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Link to="/en/notary" className="rounded-full bg-navy-deep px-4 py-2 text-sm font-bold text-white hover:bg-navy-deep/90">
                  All Notary services
                </Link>
                <Link to="/en/apostille" className="rounded-full bg-white px-4 py-2 text-sm font-bold text-navy-deep ring-1 ring-slate-300 hover:bg-slate-50">
                  Continue to Apostille
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      <RelatedNotaryLinks context="coverage" lang="en" />
    <RelatedHubGrid locale="en" /></main>
  );
}

